import { graphql } from 'gql.tada'
import { ProjectFileLink } from 'types'
import { ProjectFileSection, ProjectFileUsage } from '../../utils/enums'
import { ProjectFileUsageMeta } from '../../utils/projectFiles'

import type { GqlClient } from '@/types/gqlClient'
import { copyFileViaAction } from '@/features/file-upload/services/FileOperationMutations'

// After the #284 migration, `section` is no longer persisted. Callers that used to
// filter by section now get the same result by filtering on the set of usages that
// map to that section in the per-usage metadata.
const usagesForSection = (section: ProjectFileSection): ProjectFileUsage[] =>
  Object.entries(ProjectFileUsageMeta)
    .filter(([key, meta]) => meta.section === section && key !== ProjectFileUsage.UNDEFINED)
    .map(([key]) => key as ProjectFileUsage)


export type ProjectFileToSortType = Pick<
  ProjectFileLink,
  | 'project_id'
  | 'file'
  | 'account_visibility'
  | 'status'
  | 'use'
  | 'comment'
>

export const listProjectFilesToSort = (
  gqlClient: GqlClient,
  projectID: number
): Promise<Array<ProjectFileToSortType>> => {
  const query = graphql(`
    query ($project_id: Int) {
      project_files(
        where: {
          _and: {
            # The to-sort inbox is files with no usage assigned yet (use IS
            # NULL). UNDEFINED is now a real category ("Autres documents"),
            # not the unassigned marker.
            use: { _is_null: true }
            project_id: { _eq: $project_id }
            status: { _is_null: true }
            file: { status: { _eq: COMPLETE }, enabled: { _eq: true } }
          }
        }
      ) {
        project_id
        file {
          id
          mime_type
          name
          size
          updated_at
          uploader {
            account_type
          }
        }
        account_visibility
        status
        use
        comment
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
    })
    .then((data) =>
      Promise.resolve(data.project_files as Array<ProjectFileToSortType>)
    )
}

export const getProjectFile = (
  gqlClient: GqlClient,
  projectID: number,
  fileID: string
): Promise<ProjectFileLink> => {
  const query = graphql(`
    query ($file_id: uuid!, $project_id: Int!) {
      project_files_by_pk(file_id: $file_id, project_id: $project_id) {
        status
        use
        comment
        file {
          id
          name
          status
          size
          metadata
          etag
          mime_type
          updated_at
          uploader {
            account_type
          }
        }
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
      file_id: fileID,
    })
    .then((data) => {
      if (!data.project_files_by_pk) {
        throw new Error('Project file not found')
      }
      return Promise.resolve(data.project_files_by_pk as unknown as ProjectFileLink)
    })
}

export const validateProjectFile = (
  gqlClient: GqlClient,
  projectID: number,
  fileID: string,
  comment: string | undefined
): Promise<ProjectFileLink> => {
  const query = graphql(`
    mutation ($file_id: uuid!, $project_id: Int!, $comment: String) {
      update_project_files_by_pk(
        pk_columns: { file_id: $file_id, project_id: $project_id }
        _set: { comment: $comment, status: ACCEPTED }
      ) {
        account_visibility
        comment
        file {
          id
          mime_type
          name
          size
          updated_at
          uploader {
            account_type
          }
        }
        project_id
        status
        use
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
      file_id: fileID,
      comment: comment,
    })
    .then((data) => {
      if (!data.update_project_files_by_pk) {
        throw new Error('Failed to validate project file')
      }
      return Promise.resolve(data.update_project_files_by_pk as ProjectFileLink)
    })
}

export const rejectProjectFile = (
  gqlClient: GqlClient,
  projectID: number,
  fileID: string,
  comment: string | undefined
): Promise<ProjectFileLink> => {
  const query = graphql(`
    mutation ($file_id: uuid!, $project_id: Int!, $comment: String) {
      update_project_files_by_pk(
        pk_columns: { file_id: $file_id, project_id: $project_id }
        _set: { comment: $comment, status: REJECTED }
      ) {
        account_visibility
        comment
        file {
          id
          mime_type
          name
          size
          updated_at
          uploader {
            account_type
          }
        }
        project_id
        status
        use
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
      file_id: fileID,
      comment: comment,
    })
    .then((data) => {
      if (!data.update_project_files_by_pk) {
        throw new Error('Failed to reject project file')
      }
      return Promise.resolve(data.update_project_files_by_pk as ProjectFileLink)
    })
}

/**
 * Puts a settled file back in review — status returns to NULL, which the
 * documents page renders as "À vérifier".
 *
 * This is the undo for a mis-clicked Valider/Rejeter: the two decision buttons
 * sit side by side and disappear the moment either is pressed, so without this
 * the only way back was to delete the file and have the commercial re-upload it.
 * The comment goes with the status because it justified a decision that no
 * longer stands.
 */
export const resetProjectFileStatus = (
  gqlClient: GqlClient,
  projectID: number,
  fileID: string
): Promise<ProjectFileLink> => {
  const query = graphql(`
    mutation ($file_id: uuid!, $project_id: Int!) {
      update_project_files_by_pk(
        pk_columns: { file_id: $file_id, project_id: $project_id }
        _set: { comment: null, status: null }
      ) {
        account_visibility
        comment
        file {
          id
          mime_type
          name
          size
          updated_at
          uploader {
            account_type
          }
        }
        project_id
        status
        use
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
      file_id: fileID,
    })
    .then((data) => {
      if (!data.update_project_files_by_pk) {
        throw new Error('Failed to reset project file status')
      }
      return Promise.resolve(data.update_project_files_by_pk as ProjectFileLink)
    })
}

export const updateProjectFileUsage = (
  gqlClient: GqlClient,
  projectID: number,
  fileID: string,
  use: ProjectFileUsage
): Promise<ProjectFileLink> => {
  const query = graphql(`
    mutation (
      $file_id: uuid!
      $project_id: Int!
      $use: project_files_uses_enum!
    ) {
      update_project_files_by_pk(
        pk_columns: { file_id: $file_id, project_id: $project_id }
        _set: { use: $use }
      ) {
        account_visibility
        comment
        file {
          id
          mime_type
          name
          size
          updated_at
          uploader {
            account_type
          }
          thumbnail
        }
        project_id
        status
        use
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
      file_id: fileID,
      // Shared ProjectFileUsageType is the union of every known usage across
      // backend workspaces; Hasura's `project_files_uses_enum` is narrower
      // (DB-persisted subset). Hasura rejects unknown values at runtime.
      use: use as never,
    })
    .then((data) => {
      if (!data.update_project_files_by_pk) {
        throw new Error('Failed to update project file usage')
      }
      return Promise.resolve(data.update_project_files_by_pk as ProjectFileLink)
    })
}

export type ProjectFilesByUsage = Pick<
  ProjectFileLink,
  'project_id' | 'file' | 'status' | 'use' | 'comment'
>

// Backward-compatibility alias — `section` is no longer persisted, so this type
// is identical to `ProjectFilesByUsage`.
export type ProjectFilesBySection = ProjectFilesByUsage

export const getProjectFilesOfSection = (
  gqlClient: GqlClient,
  projectID: number,
  section: ProjectFileSection
): Promise<Array<ProjectFilesBySection>> => {
  const usages = usagesForSection(section)
  if (usages.length === 0) return Promise.resolve([])
  return getProjectFilesOfUsages(gqlClient, projectID, usages)
}

export type UrbanAnalysisFilesResult = {
  hasLinkedUA: boolean
  urbanAnalysisId: number | null
  files: Array<ProjectFilesBySection>
}

export const getUrbanAnalysisFilesOfSection = (
  gqlClient: GqlClient,
  projectID: number,
  section: ProjectFileSection
): Promise<UrbanAnalysisFilesResult> => {
  const usages = usagesForSection(section)
  if (usages.length === 0) {
    return Promise.resolve({ hasLinkedUA: false, urbanAnalysisId: null, files: [] })
  }
  const query = `
    query ($project_id: Int!) {
      urban_analysis(where: { project_id: { _eq: $project_id } }, limit: 1) {
        id
        urban_analysis_files(
          order_by: { file: { created_at: asc } }
          where: {
            use: { _in: [${usages.join(',')}] }
            file: { enabled: { _eq: true }, status: { _eq: COMPLETE } }
          }
        ) {
          use
          comment
          status
          file {
            id
            mime_type
            name
            size
            thumbnail
            updated_at
          }
        }
      }
    }
  `
  return gqlClient
    .request(query, { project_id: projectID })
    .then((data: unknown) => {
      const result = data as {
        urban_analysis: Array<{
          id: number
          urban_analysis_files: Array<ProjectFilesBySection>
        }>
      }
      const ua = result.urban_analysis?.[0]
      const uaFiles = ua?.urban_analysis_files || []
      return {
        hasLinkedUA: !!ua,
        urbanAnalysisId: ua?.id ?? null,
        files: uaFiles.map((f) => ({ ...f, project_id: projectID })),
      }
    })
}

export const getProjectFilesOfUsages = (
  gqlClient: GqlClient,
  projectID: number,
  usages: Array<string>
): Promise<Array<ProjectFilesByUsage>> => {
  // This query needs to remain as a template string due to dynamic array interpolation
  const query = `
  query ($project_id: Int) {
    project_files(
      order_by: {file: {created_at: asc}},
      where: {
        _and: {
          use: {_in: [${usages.join(',')}]},
          project_id: {_eq: $project_id},
          file: {enabled: {_eq: true}, status: {_eq: COMPLETE}}}
      }
    ) {
      project_id
      use
      comment
      status
      file {
        id
        mime_type
        name
        size
        thumbnail
        updated_at
        created_at
      }
    }
  }
    `
  return gqlClient
    .request(query, {
      project_id: projectID,
    })
    .then((data: unknown) => {
      const result = data as { project_files: Array<ProjectFilesByUsage> }
      return Promise.resolve(result.project_files)
    })
}

export type ProjectLastFileByUsage = {
  project_files: Array<ProjectFilesByUsage>
  use: string
}
export const getProjectLastFileByUsages = (
  gqlClient: GqlClient,
  projectID: number,
  usages: Array<string>
): Promise<Array<ProjectLastFileByUsage>> => {
  // This query needs to remain as a template string due to dynamic array interpolation
  const query = `
  query ($project_id: Int) {
    project_files_uses(
      where: {
        value: {_in: [${usages.join(',')}]}
      }
    ) {
      project_files(
        order_by: {
          file: {created_at: desc_nulls_last}
        },
        where: {
          project_id: {_eq: $project_id}
          file: { enabled: {_eq: true}, status: {_eq: COMPLETE}}
        }
        limit: 1
      ) {
        project_id
        use
        comment
        status
        file {
          id
          mime_type
          name
          size
          thumbnail
          updated_at
          metadata
        }
      }
      use: value
    }
  }
    `
  return gqlClient
    .request(query, {
      project_id: projectID,
    })
    .then((data: unknown) => {
      const result = data as { project_files_uses: Array<ProjectLastFileByUsage> }
      return Promise.resolve(result.project_files_uses)
    })
}

export type ProjectFilesUsageCount = Pick<
  ProjectFileLink,
  'use' | 'status'
>

export const countProjectFilesByUsage = (
  gqlClient: GqlClient,
  projectID: number
): Promise<Array<ProjectFilesUsageCount>> => {
  const query = graphql(`
    query ($project_id: Int) {
      project_files(
        where: {
          _and: {
            project_id: { _eq: $project_id }
            use: { _neq: UNDEFINED }
            file: { enabled: { _eq: true }, status: { _eq: COMPLETE } }
          }
        }
      ) {
        use
        status
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
    })
    .then((data) =>
      Promise.resolve(data.project_files as Array<ProjectFilesUsageCount>)
    )
}

export const countAllFilesByUsage = async (
  gqlClient: GqlClient,
  projectID: number
): Promise<Array<ProjectFilesUsageCount>> => {
  const projectFiles = await countProjectFilesByUsage(gqlClient, projectID)

  const uaQuery = `
    query ($project_id: Int!) {
      urban_analysis(where: { project_id: { _eq: $project_id } }, limit: 1) {
        urban_analysis_files(
          where: {
            use: { _neq: UNDEFINED }
            file: { enabled: { _eq: true }, status: { _eq: COMPLETE } }
          }
        ) {
          use
          status
        }
      }
    }
  `
  try {
    const uaData = await gqlClient.request(uaQuery, { project_id: projectID }) as {
      urban_analysis: Array<{ urban_analysis_files: Array<ProjectFilesUsageCount> }>
    }
    const uaFiles = uaData.urban_analysis?.[0]?.urban_analysis_files || []
    return [...projectFiles, ...uaFiles]
  } catch {
    return projectFiles
  }
}

export const getUrbanAnalysisReport = (
  gqlClient: GqlClient,
  projectID: number
): Promise<{ file: { id: string; created_at: string } }> => {
  const query = graphql(`
    query ($project_id: Int) {
      project_files(
        order_by: { file: { created_at: desc } }
        where: {
          _and: {
            project_id: { _eq: $project_id }
            use: { _eq: URBAN_ANALYSIS }
            status: { _eq: ACCEPTED }
            file: { enabled: { _eq: true }, status: { _eq: COMPLETE } }
          }
        }
        limit: 1
      ) {
        file {
          id
          created_at
        }
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
    })
    .then((data) =>
      Promise.resolve(
        data.project_files[0] as {
          file: { id: string; created_at: string }
        }
      )
    )
}

export const copyProjectFile = (gqlClient: GqlClient, projectID: number, fileID: string) => {
  return copyFileViaAction(gqlClient, projectID, fileID)
}

/** A dossier file's content fingerprint, for duplicate detection. */
export type ProjectFileHash = { id: string; name: string; etag: string | null }

/**
 * Every enabled file linked to a project, with its content fingerprint
 * (`files.etag` — the S3 object ETag, which for our single-part uploads is the
 * content MD5). Used to refuse re-uploading a file the dossier already holds.
 *
 * No `use` filter and no COMPLETE filter: the inbox (use = NULL) and any freshly
 * assigned file count as "already present" too. Files still processing carry a
 * null etag and are simply skipped by the caller.
 */
export const getProjectFileHashes = (
  gqlClient: GqlClient,
  projectID: number
): Promise<Array<ProjectFileHash>> => {
  const query = graphql(`
    query ($project_id: Int!) {
      project_files(
        where: {
          project_id: { _eq: $project_id }
          file: { enabled: { _eq: true } }
        }
      ) {
        file {
          id
          name
          etag
        }
      }
    }
  `)
  return gqlClient
    .request(query, { project_id: projectID })
    .then((data) =>
      (data.project_files ?? [])
        .map((pf) => pf.file)
        .filter((f): f is NonNullable<typeof f> => f != null)
        .map((f) => ({
          id: f.id as unknown as string,
          name: f.name,
          etag: f.etag ?? null,
        }))
    )
}

export const getCerfa = (
  gqlClient: GqlClient,
  projectID: number
): Promise<{ file: { id: string; created_at: string } }> => {
  const query = graphql(`
    query ($project_id: Int) {
      project_files(
        order_by: { file: { created_at: desc } }
        where: {
          _and: {
            project_id: { _eq: $project_id }
            use: { _in: [CERFA_DP, CERFA_PC, CERFA_PCM, CERFA_13703_11, CERFA_13406_12, CERFA_13411_11] }
            status: { _eq: ACCEPTED }
            file: { enabled: { _eq: true }, status: { _eq: COMPLETE } }
          }
        }
        limit: 1
      ) {
        file {
          id
          created_at
        }
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
    })
    .then((data) =>
      Promise.resolve(
        data.project_files[0] as {
          file: { id: string; created_at: string }
        }
      )
    )
}

export const assignFileUsage = async (
  gqlClient: GqlClient,
  projectID: number,
  fileId: string,
  usage: string
): Promise<void> => {
  const mutation = `
    mutation AssignFileUsage($projectId: Int!, $fileId: String!, $usage: String!) {
      assignFileUsage(projectId: $projectId, fileId: $fileId, usage: $usage) {
        success
        target
      }
    }
  `
  await gqlClient.request(mutation, {
    projectId: projectID,
    fileId,
    usage,
  })
}

export const getMatrix = (
  gqlClient: GqlClient,
  projectID: number
): Promise<{ file: { id: string; created_at: string } }> => {
  const query = graphql(`
    query ($project_id: Int) {
      project_files(
        order_by: { file: { created_at: desc } }
        where: {
          _and: {
            project_id: { _eq: $project_id }
            use: { _in: [PC_MATRIX, DP_MATRIX] }
            status: { _eq: ACCEPTED }
            file: { enabled: { _eq: true }, status: { _eq: COMPLETE } }
          }
        }
        limit: 1
      ) {
        file {
          id
          created_at
        }
      }
    }
  `)
  return gqlClient
    .request(query, {
      project_id: projectID,
    })
    .then((data) =>
      Promise.resolve(
        data.project_files[0] as {
          file: { id: string; created_at: string }
        }
      )
    )
}
